'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import {
  CalendarIcon,
  EyeIcon,
  MoreHorizontalIcon,
  PlusIcon,
  ShoppingCartIcon,
  SquareMenuIcon,
  Trash2Icon,
  UserIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog } from '@/components/partials'
import { LoadingState, SortableHeader } from '@/components/partials'
import { OrderStatusBadge, OrdersFilters } from '@/components/partials/orders'
import { useOrders, useBulkDeleteOrders, useDeleteOrder } from '@/hooks'
import { OrdersFilterParams } from '@/types/order'
import { OrderStatusEnum, SortEnum } from '@/enums'
import { formatDateTime, formatMoney } from '@/lib/utils'
import { useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([])
  const [deleteOrderId, setDeleteOrderId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'ORDERS', 'CREATE')
  const canShow = hasPermission(currentUser, 'ORDERS', 'SHOW')
  const canDelete = hasPermission(currentUser, 'ORDERS', 'DELETE')

  const { mutate: deleteOrderMutate, isPending: isDeleting } = useDeleteOrder()
  const { mutate: bulkDeleteOrdersMutate, isPending: isBulkDeleting } = useBulkDeleteOrders()

  const filters = useMemo<OrdersFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const statusesParam = params.get('statuses')
    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: (params.get('sortBy') as OrdersFilterParams['sortBy']) || 'createdAt',
      q: params.get('q') || undefined,
      statuses: statusesParam ? (statusesParam.split(',').filter(Boolean) as OrderStatusEnum[]) : undefined,
      customerType: params.get('customerType') || undefined,
      userIds: params.get('userIds') ? params.get('userIds')!.split(',').filter(Boolean) : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useOrders(filters)

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<OrdersFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, String(value))
        }
      }
    })
    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<OrdersFilterParams>) => updateUrlParams(newFilters)
  const handleResetFilters = () => router.push('/orders')
  const handleSort = (sortBy: string, sort: SortEnum) =>
    handleFiltersChange({ sortBy: sortBy as OrdersFilterParams['sortBy'], sort, page: 1 })
  const handlePageChange = (page: number) => handleFiltersChange({ page })
  const handlePageSizeChange = (size: number) => handleFiltersChange({ size, page: 1 })
  const handleView = (id: string) => router.push(`/orders/${id}`)

  const handleSelectAll = () => {
    if (data?.items) {
      if (selectedOrderIds.length === data.items.length) {
        setSelectedOrderIds([])
      } else {
        setSelectedOrderIds(data.items.map(o => o.id))
      }
    }
  }

  const handleToggleSelection = (id: string) => {
    setSelectedOrderIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const handleBulkDelete = () => {
    if (selectedOrderIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteOrders = () => {
    if (selectedOrderIds.length > 0) {
      bulkDeleteOrdersMutate(
        { ids: selectedOrderIds },
        {
          onSuccess: () => {
            setSelectedOrderIds([])
            setBulkDeleteConfirmOpen(false)
          },
          onError: () => {
            setBulkDeleteConfirmOpen(false)
          }
        }
      )
    }
  }

  const handleDelete = (id: string) => setDeleteOrderId(id)

  const confirmDeleteOrder = () => {
    if (deleteOrderId) {
      deleteOrderMutate(deleteOrderId, {
        onSuccess: () => setDeleteOrderId(null),
        onError: () => setDeleteOrderId(null)
      })
    }
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem
    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(id)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('order_details')}
          </ItemComponent>
        )}

        {canDelete && (
          <ItemComponent
            className="text-red-600 hover:text-red-700"
            onClick={e => {
              e.stopPropagation()
              handleDelete(id)
            }}>
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCartIcon className="h-5 w-5" />
                {t('orders')}
              </CardTitle>
              <CardDescription>{t('orders_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canDelete && selectedOrderIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedOrderIds.length})</span>
                  </div>
                </Button>
              )}
              {canCreate && (
                <Button size="sm" onClick={() => router.push('/orders/create')}>
                  <PlusIcon className="h-4 w-4" />
                  <span className="hidden lg:inline">{t('create_order')}</span>
                </Button>
              )}
              <OrdersFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          {canDelete && (
                            <Checkbox
                              checked={data.items.length > 0 && selectedOrderIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              className="cursor-pointer"
                            />
                          )}
                        </TableHead>
                        <TableHead className="min-w-36">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('order_number')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40">
                          <SortableHeader
                            sortBy="customerFirstname"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <UserIcon className="h-4 w-4" />
                            {t('customer')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-32 text-center">
                          <SortableHeader
                            sortBy="customerType"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('customer_type')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-32 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('order_status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-24 text-center">
                          <SortableHeader
                            sortBy="itemCount"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('item_count')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-32 text-right">
                          <SortableHeader
                            sortBy="total"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('total_amount')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((order, index) => (
                          <ContextMenu key={order.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(order.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedOrderIds.includes(order.id)}
                                        onCheckedChange={() => handleToggleSelection(order.id)}
                                        onClick={e => e.stopPropagation()}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{order.id}</TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{order.customerName}</p>
                                    <p className="text-muted-foreground text-xs">{order.customerEmail}</p>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  {t(`customer_type_${order.customerType}`)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <OrderStatusBadge status={order.status} />
                                </TableCell>
                                <TableCell className="text-center">{order.itemCount}</TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatMoney(
                                    {
                                      amount: order.total,
                                      currencyCode: order.currencyCode,
                                      currencySymbol: order.currencySymbol
                                    },
                                    locale
                                  )}
                                </TableCell>
                                <TableCell className="text-center">{formatDateTime(order.createdAt, locale)}</TableCell>
                                <TableCell className="text-center">{formatDateTime(order.updatedAt, locale)}</TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={e => e.stopPropagation()}
                                        className="h-8 w-8 p-0">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(order.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(order.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteOrderId}
          onCancel={() => setDeleteOrderId(null)}
          onConfirm={confirmDeleteOrder}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteOrders}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
